/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import cn from "classnames";
import { DropzoneOptions, FileError, useDropzone } from "react-dropzone";

import AvatarPreset from "assets/avatarUploder.png";
import Stack from "components/ui/Stack";
import Text from "components/ui/Text";
import { InfoIcon } from "components/icons";
import defaultValidator from "./defaultValidator";

interface IDropZone extends DropzoneOptions {
  acceptedFormats?: string[] | string;
  style?: string;
  customStyledContent?: React.ReactNode;
  className?: string;
  multiple?: boolean;
  customUploadText?: string;
  inputId?: string;
  errorMessage?: string | null;
  validator?: (file: File) => FileError | FileError[] | null;
}

const DropZone = ({
  onDrop,
  acceptedFormats,
  validator: customValidator,
  style,
  customStyledContent,
  className,
  multiple,
  customUploadText,
  inputId,
  errorMessage,
}: IDropZone) => {
  const options = {
    onDrop,
    multiple,
  };

  if (!!acceptedFormats && acceptedFormats.length > 0) {
    Object.assign(options, { accept: acceptedFormats });
  }

  const { getRootProps, getInputProps } = useDropzone({
    ...options,
    validator: customValidator
      ? customValidator
      : (file: File) => defaultValidator(file, acceptedFormats),
  });

  return (
    <>
      <div
        className={cn(
          className,
          "border-2 border-c-gray-500 relative w-full sm:min-w-[340px] h-[270px] border-dashed",
          {
            "border-none sm:min-w-[200px]": style === "avatar",
          },
        )}
        test-id="p9q-AyYr"
      >
        <div
          {...getRootProps()}
          className={cn(
            "absolute left-0 right-0 top-0 bottom-0 h-full w-full flex justify-center items-center",
          )}
          role="button"
          test-id="WZX1IYaG"
        >
          <input {...getInputProps()} test-id="J4F8vKe_" id={inputId} />

          {customStyledContent ? (
            customStyledContent
          ) : (
            <>
              {style ? (
                <>
                  <img
                    src={AvatarPreset}
                    alt="Avatar"
                    className="w-[200px] h-[200px]"
                    test-id="AISZlQ3P"
                  />
                </>
              ) : (
                <div className="text-white text-center" test-id="8X2lreo1">
                  <Stack
                    className="h-full space-y-2 absolute inset-0 items-center justify-center"
                    test-id="2uTfHKwB"
                  >
                    <span
                      className="flex bg-[#FFFFFF0D] rounded-full p-2.5 text-black"
                      test-id="RJZ6GI-_"
                    >
                      <svg
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        test-id="dh9TNo9h"
                      >
                        <path
                          d="M6.5 20.5C4.98333 20.5 3.68767 19.975 2.613 18.925C1.53767 17.875 1 16.5917 1 15.075C1 13.775 1.39167 12.6167 2.175 11.6C2.95833 10.5833 3.98333 9.93333 5.25 9.65C5.66667 8.11667 6.5 6.875 7.75 5.925C9 4.975 10.4167 4.5 12 4.5C13.95 4.5 15.604 5.179 16.962 6.537C18.3207 7.89567 19 9.55 19 11.5C20.15 11.6333 21.1043 12.129 21.863 12.987C22.621 13.8457 23 14.85 23 16C23 17.25 22.5627 18.3127 21.688 19.188C20.8127 20.0627 19.75 20.5 18.5 20.5H13C12.45 20.5 11.9793 20.3043 11.588 19.913C11.196 19.521 11 19.05 11 18.5V13.35L9.4 14.9L8 13.5L12 9.5L16 13.5L14.6 14.9L13 13.35V18.5H18.5C19.2 18.5 19.7917 18.2583 20.275 17.775C20.7583 17.2917 21 16.7 21 16C21 15.3 20.7583 14.7083 20.275 14.225C19.7917 13.7417 19.2 13.5 18.5 13.5H17V11.5C17 10.1167 16.5127 8.93733 15.538 7.962C14.5627 6.98733 13.3833 6.5 12 6.5C10.6167 6.5 9.43767 6.98733 8.463 7.962C7.48767 8.93733 7 10.1167 7 11.5H6.5C5.53333 11.5 4.70833 11.8417 4.025 12.525C3.34167 13.2083 3 14.0333 3 15C3 15.9667 3.34167 16.7917 4.025 17.475C4.70833 18.1583 5.53333 18.5 6.5 18.5H9V20.5H6.5Z"
                          fill="white"
                          test-id="oCrQ4lWs"
                        />
                      </svg>
                    </span>

                    <Text className="flex flex-col items-center text-sm">
                      <span className="font-bold">
                        {customUploadText ?? "Click to upload"}
                      </span>
                      <span className="text-[#989898]">or drag and drop</span>
                    </Text>
                  </Stack>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {errorMessage && (
        <div
          className="flex items-center gap-x-1 mt-1 text-alert text-sm"
          test-id="error-message"
        >
          <InfoIcon width="14" height="14" fill="#E46D64" />
          <Text as="span" className="text-alert text-xs">
            {errorMessage}
          </Text>
        </div>
      )}
    </>
  );
};

export default DropZone;
